import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, MapPin, Mail, Globe } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Separator } from "./ui/separator";
import { businessInfo } from "../data/mock";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About Us" },
  { path: "/services", label: "Services" },
  { path: "/track", label: "Track Shipment" },
  { path: "/contact", label: "Contact Us" },
];

export const Header = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top Bar */}
      <div className="bg-brand-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between text-xs sm:text-sm">
          <div className="flex items-center gap-4 flex-wrap">
            <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-1.5 hover:text-brand-light transition-colors">
              <Phone className="w-3.5 h-3.5" />
              <span>{businessInfo.phone}</span>
            </a>
            <a href={`mailto:${businessInfo.email}`} className="hidden sm:flex items-center gap-1.5 hover:text-brand-light transition-colors">
              <Mail className="w-3.5 h-3.5" />
              <span>{businessInfo.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              <span>Mansarovar Garden, New Delhi</span>
            </div>
            <a href={`https://${businessInfo.website}`} target="_blank" rel="noopener noreferrer" className="hidden lg:flex items-center gap-1.5 hover:text-brand-light transition-colors">
              <Globe className="w-3.5 h-3.5" />
              <span>{businessInfo.website}</span>
            </a>
            <a
              href={businessInfo.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-[#25D366] text-white px-3 py-1 rounded hover:bg-[#1da851] transition-colors font-medium"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src={businessInfo.logoUrl}
              alt="Om Sai Express Logo"
              className="w-11 h-11 md:w-14 md:h-14 rounded-full object-cover shadow-md"
            />
            <div>
              <h1 className="text-lg md:text-xl font-bold text-brand-dark leading-tight" style={{ fontFamily: "'Lora', serif" }}>Om Sai Express</h1>
              <p className="text-[10px] md:text-xs text-gray-500 tracking-wide">COURIER & LOGISTICS</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "bg-brand text-white"
                    : "text-gray-700 hover:text-brand-dark hover:bg-brand-lightest"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              asChild
              className="bg-brand hover:bg-brand-dark text-white font-semibold shadow-md"
              size="sm"
            >
              <a href={`tel:${businessInfo.phone}`}>
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-brand-dark">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <img
                      src={businessInfo.logoUrl}
                      alt="Om Sai Express Logo"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <h2 className="text-base font-bold text-brand-dark" style={{ fontFamily: "'Lora', serif" }}>Om Sai Express</h2>
                      <p className="text-[10px] text-gray-500">COURIER & LOGISTICS</p>
                    </div>
                  </div>
                  <Separator className="mb-4" />
                  <nav className="flex flex-col gap-1">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileOpen(false)}
                        className={`px-4 py-3 rounded text-sm font-medium transition-colors ${
                          location.pathname === link.path
                            ? "bg-brand text-white"
                            : "text-gray-700 hover:bg-brand-lightest hover:text-brand-dark"
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </nav>
                  <Separator className="my-4" />
                  <div className="flex flex-col gap-3">
                    <Button asChild className="bg-brand hover:bg-brand-dark text-white w-full">
                      <a href={`tel:${businessInfo.phone}`}>
                        <Phone className="w-4 h-4" />
                        Call Now – {businessInfo.phone}
                      </a>
                    </Button>
                    <Button asChild className="bg-[#25D366] hover:bg-[#1da851] text-white w-full">
                      <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp Us
                      </a>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};
